// src/pages/Workforce.jsx
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';

import workforceImg from '../assets/images/who-we-are.png';
import service3Img from '../assets/images/service3.jpg';

const MODELS = [
  { title: 'Contract staffing', desc: 'Short to long-term contract professionals placed with your team for defined project durations.', tags: ['Flexible duration', 'Quick deployment', 'Vetted talent'] },
  { title: 'Project-based delivery', desc: 'Dedicated teams assigned to specific outcomes with built-in oversight and accountability.', tags: ['Outcome-focused', 'Internal QA', 'Milestone tracking'] },
  { title: 'Workforce augmentation', desc: 'Extend your existing team with trained professionals who integrate into your delivery workflows.', tags: ['Seamless integration', 'Scalable', 'Performance oversight'] },
  { title: 'Long-term staffing', desc: 'Stable, long-term workforce partners who understand your systems, culture and compliance needs.', tags: ['Continuity', 'Institutional knowledge', 'Compliance ready'] },
];

const ROLES = ['Software Engineers', 'QA Analysts', 'Business Analysts', 'UX/UI Designers', 'DevOps Engineers', 'IT Support Specialists', 'Project Managers', 'Cloud Architects', 'Security Analysts', 'Data Analysts'];

export default function Workforce() {
  return (
    <>
      <SEO
        title="Workforce Solutions"
        description="District Sourcing Tech provides workforce augmentation, contract staffing and long-term workforce delivery for government and enterprise IT environments."
        url="https://districtsourcingtech.com/workforce"
        keywords="workforce staffing, IT workforce, government staffing, contract IT professionals, workforce augmentation"
      />
      <Navbar variant="light" />

      {/* Hero */}
      <section className="career-hero">
        <div className="career-hero__bg">
          <img src={workforceImg} alt="Workforce professionals" />
        </div>
        <div className="career-hero__overlay" />
        <div className="career-hero__card">
          <p className="career-hero__label">Workforce Model</p>
          <h1 className="title-1" style={{ marginBottom: '12px', marginTop: '4px' }}>Workforce</h1>
          <p className="body-lg" style={{ color: 'var(--token-desc)', marginBottom: '32px' }}>
            Built, supplied, and supported workforces reduce delivery risk and maintain continuity across engagements.
          </p>
          <Link to="/contact" className="btn btn-primary">Get started</Link>
        </div>
      </section>

      {/* Delivery Models */}
      <section className="section">
        <div style={{ maxWidth: '1376px', margin: '0 auto' }}>
          <Reveal>
            <h2 className="title-2" style={{ marginBottom: '16px' }}>Delivery models</h2>
            <p className="body-lg" style={{ color: 'var(--token-desc)', marginBottom: '64px', maxWidth: '600px' }}>
              We operate across four workforce delivery models, each designed for different organizational needs and timelines.
            </p>
          </Reveal>
          <div className="grid-2-col">
            {MODELS.map((m, i) => (
              <Reveal
                key={m.title}
                delay={i * 100}
                style={{
                  background: 'var(--shades-200)',
                  borderRadius: '8px',
                  padding: '40px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                }}
              >
                <div style={{
                  background: 'var(--primary-100)', border: '1.5px solid var(--primary-200)',
                  borderRadius: '9999px', width: '48px', height: '48px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--primary-500)',
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                <div>
                  <h3 className="title-5" style={{ marginBottom: '8px' }}>{m.title}</h3>
                  <p className="body-lg" style={{ color: 'var(--token-desc)' }}>{m.desc}</p>
                </div>
                <div className="service-tags">
                  {m.tags.map(t => <span key={t} className="service-tag">{t}</span>)}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Roles */}
      <section className="section section--alt">
        <div style={{ maxWidth: '1376px', margin: '0 auto' }}>
          <div className="two-col">
            <Reveal className="two-col__image">
              <img src={service3Img} alt="Workforce professionals" loading="lazy" />
            </Reveal>
            <Reveal delay={150} className="two-col__content">
              <div>
                <h2 className="title-2" style={{ marginBottom: '16px' }}>Roles we fill</h2>
                <p className="body-lg" style={{ color: 'var(--token-desc)', marginBottom: '32px' }}>
                  We maintain a pipeline of trained, vetted professionals across the technology and IT services spectrum.
                </p>
                <div className="service-tags">
                  {ROLES.map(r => <span key={r} className="service-tag">{r}</span>)}
                </div>
              </div>
              <Link to="/contact" className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                Discuss workforce needs →
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
